import { AlertTriangle, Inbox, Lock, RotateCw } from 'lucide-react'
import { SkeletonRows } from '../common/Skeleton'
import './DataState.css'

/**
 * DataState — одно место, где живут четыре состояния данных.
 *
 * `loading` · `empty` · `error` · `forbidden`
 *
 * Зачем отдельный компонент, если есть EmptyState: EmptyState — это блок
 * текста по центру, он не различает причину. До волны 2 «нет кандидатов»,
 * «не загрузилось» и «вам не положено видеть» выглядели на экране
 * одинаково — пустым местом, и человек не мог понять, ждать ему, жать
 * «повторить» или идти к администратору.
 *
 * Состояние объявляется атрибутом `data-page-state`. Это не отладочный
 * след: по нему сторож вёрстки отличает экран с данными от экрана без
 * данных и не меряет пустоту, считая её успехом (#5433).
 */

const KINDS = {
    loading: { icon: null, title: 'Загружаем данные…', role: 'status' },
    empty: { icon: Inbox, title: 'Пока пусто', role: 'status' },
    error: { icon: AlertTriangle, title: 'Не удалось загрузить данные', role: 'alert' },
    forbidden: { icon: Lock, title: 'Доступ ограничен', role: 'status' },
}

const DEFAULT_HINT = {
    error: 'Проверьте соединение и повторите. Если повторяется — сообщите администратору.',
    forbidden: 'Раздел открыт не для всех ролей. Доступ выдаёт администратор Onboardix.',
}

/**
 * Какое состояние показывать по ошибке запроса. 403 — это не поломка, а отказ
 * в доступе: человеку нужно идти к администратору, а не жать «Повторить».
 * `err.status` проставляет apiFetch (api/core.js).
 */
export function errorKind(err) {
    return err?.status === 403 ? 'forbidden' : 'error'
}

/**
 * Плашка «показана сохранённая копия».
 *
 * `fetchWithFallback` при отказе сервера отдаёт прошлый ответ из
 * `localStorage` с `source: 'cache'` — и страница рисует его как свежий. У
 * того, кто раньше уже открывал раздел, копия есть всегда, поэтому экран
 * ошибки видел только тот, кто зашёл впервые. Данные при этом показать
 * правильно (лучше, чем пустой экран), а вот молчать про их возраст — нет.
 *
 * Это НЕ состояние страницы: данные на экране есть, поэтому
 * `data-page-state` плашка не объявляет.
 */
export function StaleNotice({ onRetry, hint }) {
    return (
        <div className="data-state__stale" role="status">
            <AlertTriangle size={14} aria-hidden="true" />
            <span>{hint || 'Сервер не ответил — показана сохранённая копия.'}</span>
            {onRetry ? (
                <button type="button" className="btn btn--secondary btn--sm" onClick={onRetry}>
                    <RotateCw size={13} aria-hidden="true" />
                    Обновить
                </button>
            ) : null}
        </div>
    )
}

export function DataState({
    kind = 'empty',
    title,
    hint,
    detail,
    onRetry,
    retryLabel = 'Повторить',
    action,
    rows = 4,
    inline = false,
    className = '',
    ...rest
}) {
    // Незнакомое имя сводится к `empty` целиком — и оформлением, и атрибутом.
    // Раньше сюда попадало сырое `kind`, и опечатка в вызове давала экран с
    // `data-page-state="нечто"`: сторож вёрстки такое значение не узнаёт и
    // считает экран заполненным, то есть меряет геометрию пустоты как успех.
    // `hasOwnProperty`, а не `KINDS[kind]`: у обычного литерала истинны и
    // унаследованные ключи, поэтому `kind="constructor"` проходил проверку,
    // уезжал в `data-page-state` сырым и делал `config` функцией `Object` —
    // экран без роли, без заголовка и с состоянием, которого нет в договоре.
    const safeKind = Object.prototype.hasOwnProperty.call(KINDS, kind) ? kind : 'empty'
    const config = KINDS[safeKind]
    const Icon = config.icon

    if (safeKind === 'loading') {
        return (
            <div
                className={`data-state ${inline ? 'data-state--inline' : ''} data-state--loading ${className}`.trim()}
                {...rest}
                data-page-state="loading"
                role="status"
                aria-live="polite"
                aria-busy="true"
            >
                <span className="data-state__sr">{title || config.title}</span>
                <div className="data-state__rows">
                    <SkeletonRows count={rows} height={14} />
                </div>
            </div>
        )
    }

    return (
        <div
            className={`data-state ${inline ? 'data-state--inline' : ''} data-state--${safeKind} ${className}`.trim()}
            {...rest}
            data-page-state={safeKind}
            role={config.role}
        >
            {Icon ? (
                <div className="data-state__icon">
                    <Icon size={20} aria-hidden="true" />
                </div>
            ) : null}
            <h2 className="data-state__title">{title || config.title}</h2>
            {hint || DEFAULT_HINT[safeKind] ? (
                <p className="data-state__hint">{hint || DEFAULT_HINT[safeKind]}</p>
            ) : null}
            {detail ? <p className="data-state__detail">{detail}</p> : null}
            {onRetry || action ? (
                <div className="data-state__actions">
                    {onRetry ? (
                        <button type="button" className="btn btn--secondary btn--sm" onClick={onRetry}>
                            <RotateCw size={14} aria-hidden="true" />
                            {retryLabel}
                        </button>
                    ) : null}
                    {action}
                </div>
            ) : null}
        </div>
    )
}

export default DataState
